import { useState, useEffect, useContext } from "react";
import { ContextAuth } from "../../../contexts/ContextAuth";
import { toast } from "react-toastify";
import { sendConfirmDispatchData } from "../../../apis/apis.dispatches/apisDispatch";

const initialOptionState = { name: "cash", description: "CONTADO", value: 0 };

const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

export function ConfirmDispatchForm({ dispatchToConfirm, setDispatchToConfirm, isOpen, onClose, onRefresh }) {
    const [method, setMethod]                       = useState(initialOptionState);
    const [paymentMethodsArray, setPaymentMethodsArray] = useState([]);
    const { auth }                                  = useContext(ContextAuth);

    useEffect(() => {
        setMethod((m) => ({ ...m, value: dispatchToConfirm.total }));
    }, [dispatchToConfirm.total]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    // Trigger dispatch when paymentMethods changes
    useEffect(() => {
        if ("physicalDistribution" in dispatchToConfirm && dispatchToConfirm.physicalDistribution !== "") {
            sendConfirmDispatchData(dispatchToConfirm).then((r) => {
                if ("documentNumber" in r) {
                    toast.success("Se generó el documento " + r.documentNumber);
                    onClose();
                    onRefresh?.();
                    setPaymentMethodsArray([]);
                }
            });
        }
    }, [dispatchToConfirm.paymentMethods]);

    const handleRemoveItem = (item) => {
        if (window.confirm("¿Está seguro de eliminar el item?"))
            setPaymentMethodsArray((prev) => prev.filter((o) => o.name !== item.name));
    };

    const handleAddMethod = () => {
        const found = paymentMethodsArray.find((o) => o.name === method.name);
        if (found) { toast.info("Ya existe este método"); return; }
        if (Number(method.value) <= 0) { toast.info("Verifique el monto"); return; }
        setPaymentMethodsArray([...paymentMethodsArray, method]);
        setMethod(initialOptionState);
    };

    const handleConfirmDispatch = () => {
        if (!window.confirm("¿Está seguro de confirmar la preventa?")) return;
        const paid  = paymentMethodsArray.reduce((s, o) => s + Number(o.value), 0);
        const total = Number(dispatchToConfirm.total);
        if (Number(paid.toFixed(2)) !== Number(total.toFixed(2))) { toast.info("Verifique el pago"); return; }
        const payments = {};
        paymentMethodsArray.forEach((item) => { payments[item.name] = Number(item.value); });
        setDispatchToConfirm({ ...dispatchToConfirm, paymentMethods: payments, userID: auth.data.user_id });
    };

    if (!isOpen) return null;

    const paid  = paymentMethodsArray.reduce((s, o) => s + Number(o.value), 0);
    const total = Number(dispatchToConfirm.total);
    const diff  = Number((paid - total).toFixed(2));

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-modal="true" role="dialog">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative z-10 w-full max-w-lg max-h-[90vh] flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-blue-600 dark:bg-blue-700 flex-shrink-0">
                    <h5 className="text-base font-semibold text-white flex items-center gap-2">
                        <i className="fas fa-check-circle"></i>
                        Confirmar pedido
                    </h5>
                    <button type="button" onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1 rounded" aria-label="Cerrar">
                        <i className="fas fa-times text-lg"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-white dark:bg-gray-800">

                    {/* Summary cards */}
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: "Items", value: dispatchToConfirm.details?.length ?? 0, color: "text-blue-700 dark:text-blue-300", bg: "bg-blue-50 dark:bg-blue-900/20" },
                            { label: "Pagado", value: `S/ ${paid.toFixed(2)}`, color: diff === 0 ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300", bg: diff === 0 ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-amber-50 dark:bg-amber-900/20" },
                            { label: "Total", value: `S/ ${total.toFixed(2)}`, color: "text-gray-800 dark:text-gray-200", bg: "bg-gray-50 dark:bg-gray-700/50" },
                        ].map(({ label, value, color, bg }) => (
                            <div key={label} className={`rounded-xl border border-gray-200 dark:border-gray-700 ${bg} px-3 py-3 text-center`}>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
                                <p className={`text-lg font-bold ${color}`}>{value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Payment method input */}
                    <div>
                        <label className={labelCls}><i className="fas fa-credit-card mr-1"></i>Métodos de pago</label>
                        <div className="flex gap-2">
                            <select
                                value={method.name}
                                onChange={(e) => {
                                    const idx = e.nativeEvent.target.selectedIndex;
                                    setMethod({ ...method, name: e.target.value, description: e.nativeEvent.target[idx].text });
                                }}
                                className={inputCls}
                            >
                                <option value="cash">CONTADO</option>
                                <option value="yape">YAPE</option>
                                <option value="bcp">BCP - DEPÓSITO</option>
                                <option value="credit">CRÉDITO</option>
                            </select>
                            <input
                                type="number"
                                value={method.value}
                                onChange={(e) => setMethod({ ...method, value: e.target.value })}
                                onFocus={(e) => e.target.select()}
                                className={`${inputCls} w-28`}
                            />
                            <button type="button" onClick={handleAddMethod}
                                className="inline-flex items-center justify-center h-10 w-10 flex-shrink-0 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                            ><i className="fas fa-plus"></i></button>
                        </div>
                    </div>

                    {/* Payment methods table */}
                    {paymentMethodsArray.length > 0 && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <table className="min-w-full text-sm border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        {["Método","Monto",""].map((h) => (
                                            <th key={h} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-center">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentMethodsArray.map((item, i) => (
                                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                            <td className="px-3 py-2 text-xs font-medium text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700">{item.description}</td>
                                            <td className="px-3 py-2 text-xs text-right font-semibold text-emerald-700 dark:text-emerald-400 border-b border-gray-100 dark:border-gray-700">S/ {Number(item.value).toFixed(2)}</td>
                                            <td className="px-3 py-2 text-xs text-center border-b border-gray-100 dark:border-gray-700">
                                                <button type="button" onClick={() => handleRemoveItem(item)}
                                                    className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                ><i className="fas fa-trash text-[10px]"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Tipo entrega */}
                    <div>
                        <label className={labelCls}><i className="fas fa-truck mr-1"></i>Tipo de entrega</label>
                        <select value={dispatchToConfirm.physicalDistribution} onChange={(e) => setDispatchToConfirm({ ...dispatchToConfirm, physicalDistribution: e.target.value })} className={inputCls}>
                            <option value="01">TIRAS</option>
                            <option value="02">SUELTAS</option>
                        </select>
                    </div>

                    {/* Tipo documento */}
                    <div>
                        <label className={labelCls}><i className="fas fa-file-alt mr-1"></i>Tipo de documento</label>
                        <select value={dispatchToConfirm.documentType} onChange={(e) => setDispatchToConfirm({ ...dispatchToConfirm, documentType: e.target.value })} className={inputCls}>
                            <option value="01">TICKET</option>
                            <option value="02">BOLETA</option>
                            <option value="03">FACTURA</option>
                        </select>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                    <button type="button" onClick={onClose} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors">
                        <i className="fas fa-times"></i>Cerrar
                    </button>
                    <button type="button" onClick={handleConfirmDispatch}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 transition-colors"
                    >
                        <i className="fas fa-check"></i>Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
}
